import { IconAngleLeft } from '../../../shared/Icon'
import history from '../../../../history'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../types/rootState'
import { defineMessages, useIntl } from 'react-intl'
import { PATH_HELPERS } from '../../../../constants/navigation'
import { ActionBar } from '../../..'
import { Box, Button, Icon, useToast } from '@homebotapp/hb-react-component-catalog'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { selectCustomerId } from '../../../../store/selectors/customerProfile'
import { useDispatch } from 'react-redux'
import { selectClientIsGuest } from '../../../../store/selectors/client'
import { setPreviouslyViewedListingId } from '../../../../actions/listings'
import { useTrackingContext } from '../../../../providers/tracking'
import { toggleAuthModal } from '../../../../actions/homeSearchAuthModal'
import { useGetFavoriteListings } from '../../../../hooks/gallery/useGetFavoriteListings'
import { useToggleFavoriteListing } from '../../../../hooks/listings/useToggleFavoriteListing'
import { hasCustomerToken, isAuthTokenSupported } from '../../../../auth'
import { clientEventSourceMapper } from '../../Utils/clientEventSourceMapper'
import { useRecoilState } from 'recoil'
import { firstFavoritePropertyState } from '../../GoogleAutoComplete/state/isFirstFavoriteProperty'
import { ListingFavoriteAlertModal } from '../../ListingFavoriteAlertModal/ListingFavoriteAlertModal'
import { useEffectOncePerSessionConditionally } from '../../../../hooks'
import { ShareListingButton } from '../../ShareListingButton/ShareListingButton'

const MSG = defineMessages({
  back: {
    id: 'HomeSearch.listings.actions.back',
    defaultMessage: 'Back'
  },
  more: {
    id: 'HomeSearch.listings.actions.more',
    defaultMessage: 'More Listings'
  },
  favorite: {
    id: 'HomeSearch.listings.actions.favorite',
    defaultMessage: 'Favorite'
  },
  favoriteImpersonating: {
    id: 'HomeSearch.listings.actions.favoriteImpersonation',
    defaultMessage: 'Favoriting listings on behalf of your clients is not supported at this time.'
  },
  toast: {
    id: 'Listing.Favorite.Status',
    defaultMessage: 'Listing {status, select, false {added} true {removed} other {added}}'
  },
  undo: {
    id: 'Listing.Favorite.undo',
    defaultMessage: 'undo'
  }
})

export interface ListingActionBarProps {
  listing?: Listing
}

export const ListingActionBar = ({ listing }: ListingActionBarProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const toast = useToast()
  const { trackingClient } = useTrackingContext()
  const isGuest = useSelector(selectClientIsGuest)
  const isImpersonating = hasCustomerToken()
  const customerId = useSelector(selectCustomerId)
  const isPublic = useSelector<RootState, boolean>(state => state.auth.isPublic)

  const { data, refetch: refetchFavoriteListings, isLoading: isLoadingFavoriteListings } = useGetFavoriteListings({})
  const favoriteListings = useMemo(() => data?.favoriteListings?.listings ?? [], [data?.favoriteListings?.listings])

  const { mutateAsync: toggleFavoriteListing } = useToggleFavoriteListing()

  const [recoilFirstFavoritePropertyState, setRecoilFirstFavoritePropertyState] =
    useRecoilState(firstFavoritePropertyState)

  const setFirstFavoriteProperty = () => {
    if (favoriteListings && favoriteListings.length === 0) {
      setRecoilFirstFavoritePropertyState('true')
    }
  }
  // set atom for first favorite property based on faoriteListings array
  useEffectOncePerSessionConditionally(
    setFirstFavoriteProperty,
    'View_listingFavoriteButton',
    isLoadingFavoriteListings === false
  )

  const isFavorite = useMemo(
    () => !!favoriteListings.find(list => list?.id === listing?.id),
    [favoriteListings, listing?.id]
  )

  const handleBackButtonClick = () => {
    if (history.length > 1) {
      dispatch(setPreviouslyViewedListingId(listing?.id))
      history.goBack()
    } else {
      const path = isPublic
        ? PATH_HELPERS.publicListings.buildPath('postal_code', listing?.address?.postalCode, {
            statuses: ['Active', 'Coming Soon']
          })
        : PATH_HELPERS.listings.buildPath(customerId, 'postal_code', listing?.address?.postalCode, {
            statuses: ['Active', 'Coming Soon']
          })

      history.push(path)
    }
  }

  return (
    <>
      <ActionBar>
        <ActionBar.Left>
          <Box bg='black' borderRadius='md' overflow='hidden' _light={{ bg: 'white' }}>
            <Button
              size='sm'
              colorScheme='secondary'
              aria-label='back'
              data-qa='buttonBack'
              leftIcon={<IconAngleLeft />}
              onClick={handleBackButtonClick}
              ml='env(safe-area-inset-left, 0)'
            >
              {history.location?.key ? intl.formatMessage(MSG.back) : intl.formatMessage(MSG.more)}
            </Button>
          </Box>
        </ActionBar.Left>
        <ActionBar.Right>
          {!isPublic && (
            <>
              <ShareListingButton listing={listing} />
              <Box bg='black' borderRadius='md' overflow='hidden' _light={{ bg: 'white' }}>
                <Button
                  colorScheme='secondary'
                  size='sm'
                  leftIcon={isFavorite ? <Icon name='heart' /> : <Icon name='heart-empty' />}
                  onClick={async () => {
                    if (isGuest || !isAuthTokenSupported()) {
                      if (isImpersonating) {
                        toast({
                          description: intl.formatMessage(MSG.favoriteImpersonating),
                          status: 'warning',
                          duration: 4000,
                          position: 'top'
                        })
                      } else {
                        dispatch(toggleAuthModal(true))
                      }
                    } else if (listing?.id) {
                      await toggleFavoriteListing({
                        toggleFavoriteListingInput: {
                          listingId: listing.id,
                          eventSource: clientEventSourceMapper(history.location.pathname)
                        }
                      })
                      await refetchFavoriteListings()
                      trackingClient?.homeFavorited()
                      if (recoilFirstFavoritePropertyState === 'true') {
                        setRecoilFirstFavoritePropertyState('pending')
                      }
                    }
                  }}
                >
                  {intl.formatMessage(MSG.favorite)}
                </Button>
              </Box>
            </>
          )}
        </ActionBar.Right>
      </ActionBar>
      {recoilFirstFavoritePropertyState !== 'false' && <ListingFavoriteAlertModal mode='initial' />}
    </>
  )
}
